import { useState, useEffect } from 'react';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import PromptInput from '@cloudscape-design/components/prompt-input';
import { fetchTabData, invokeAgent, TabDataResponse } from './agentcore';

interface AdminDashboardProps {
  userEmail: string;
  tenantId?: string;
}

const AdminDashboard = ({ userEmail, tenantId }: AdminDashboardProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tab data for the selected tenant
  const [tab1Data, setTab1Data] = useState<TabDataResponse | null>(null);
  const [tab2Data, setTab2Data] = useState<TabDataResponse | null>(null);
  const [tab3Data, setTab3Data] = useState<TabDataResponse | null>(null);

  // Chat state
  const [chatPrompt, setChatPrompt] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // Load tab data when tenantId changes
  useEffect(() => {
    if (tenantId) {
      loadTabData();
    }
  }, [tenantId]);

  const loadTabData = async () => {
    if (!tenantId) return;

    try {
      setLoading(true);
      setError('');

      // Fetch all 3 tabs in parallel for the selected tenant
      const [pulse, journey, actions] = await Promise.all([
        fetchTabData(tenantId, 'tab_pulse', 'restaurant'),
        fetchTabData(tenantId, 'tab_journey', 'restaurant'),
        fetchTabData(tenantId, 'tab_actions', 'restaurant'),
      ]);

      setTab1Data(pulse);
      setTab2Data(journey);
      setTab3Data(actions);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async () => {
    if (!chatPrompt.trim() || !tenantId) return;

    try {
      setChatLoading(true);
      const response = await invokeAgent({
        prompt: chatPrompt,
        actor_id: tenantId,
        request_type: 'chat'
      });
      setChatResponse(response.response);
      setChatPrompt('');
    } catch (err: any) {
      setChatResponse(`Error: ${err.message}`);
    } finally {
      setChatLoading(false);
    }
  };

  const renderTabData = (data: TabDataResponse | null, title: string) => {
    return (
      <div style={{
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 200px)',
        paddingRight: '8px'
      }}>
        <Box margin={{ bottom: 's' }}>
          <Header variant="h2">{title}</Header>
        </Box>
        <Container>
          {data ? (
            <Box>
              <pre style={{
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '400px',
                backgroundColor: '#f4f4f4',
                padding: '8px',
                borderRadius: '4px'
              }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            </Box>
          ) : (
            <Box textAlign="center" padding="s">
              <Spinner />
            </Box>
          )}
        </Container>
      </div>
    );
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>Loading dashboard...</Box>
      </Box>
    );
  }

  if (!tenantId) {
    return (
      <Box textAlign="center" padding="xxl">
        <Box variant="p">Select a tenant from the sidebar to view dashboard</Box>
      </Box>
    );
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`Dashboard for ${tenantId}`}
        >
          Business Dashboard
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError('')}>
            {error}
          </Alert>
        )}

        <ColumnLayout columns={4} variant="text-grid">
          {/* Column 1: Today's Pulse */}
          {renderTabData(tab1Data, "Today's Pulse")}

          {/* Column 2: Business Journey */}
          {renderTabData(tab2Data, "Business Journey")}

          {/* Column 3: Action Items */}
          {renderTabData(tab3Data, "Action Items")}

          {/* Column 4: Chat Testing */}
          <div style={{
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 200px)',
            paddingRight: '8px'
          }}>
            <Box margin={{ bottom: 's' }}>
              <Header variant="h2">Test Chat</Header>
            </Box>
            <Container>
              <SpaceBetween size="m">
                <Box variant="small" color="text-body-secondary">
                  Testing with: {tenantId}
                </Box>

                {chatResponse && (
                  <Box padding="s" style={{
                    backgroundColor: '#f4f4f4',
                    borderRadius: '4px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <Box variant="small" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                      Response:
                    </Box>
                    {chatResponse}
                  </Box>
                )}

                <PromptInput
                  value={chatPrompt}
                  onChange={({ detail }) => setChatPrompt(detail.value)}
                  onAction={handleChatSubmit}
                  placeholder="Test a question..."
                  actionButtonAriaLabel="Send"
                  actionButtonIconName="send"
                  disabled={chatLoading || !tenantId}
                />
              </SpaceBetween>
            </Container>
          </div>
        </ColumnLayout>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default AdminDashboard;
